import React, { useState } from 'react';
import { ExternalLink, Eye, EyeOff, Loader2 } from 'lucide-react';
import { fetchLinkPreview } from '../utils/linkPreview';

const Tab = ({ active, onClick, children }) => (
    <button
        onClick={onClick}
        className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
            active
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
        }`}
    >
        {children}
    </button>
);

const MetaRow = ({ label, value, highlight }) => {
    if (!value) return null;
    return (
        <tr className="border-b border-gray-800">
            <td className="py-1.5 pr-3 text-gray-500 whitespace-nowrap align-top w-40">{label}</td>
            <td className={`py-1.5 break-all ${highlight ? 'text-green-400' : 'text-gray-300'}`}>{value}</td>
        </tr>
    );
};

const GooglePreview = ({ metadata }) => (
    <div className="p-4 bg-white rounded-lg">
        <div className="flex items-center gap-2 mb-1">
            {metadata.favicon && (
                <img
                    src={metadata.favicon}
                    alt="favicon"
                    width="16"
                    height="16"
                    className="w-4 h-4 object-contain"
                    onError={e => e.target.style.display = 'none'}
                />
            )}
            <span className="text-xs text-gray-600">{metadata.site_name || new URL(metadata.url).hostname}</span>
        </div>
        <div className="text-blue-700 text-lg font-medium leading-snug hover:underline cursor-pointer line-clamp-1">
            {metadata.title}
        </div>
        <div className="text-gray-600 text-sm line-clamp-2 mt-0.5">
            {metadata.description}
        </div>
    </div>
);

const OGPreview = ({ metadata }) => (
    <div className="border border-gray-600 rounded-lg overflow-hidden bg-gray-900 max-w-sm">
        {metadata.image && (
            <img
                src={metadata.image}
                alt={metadata.title}
                className="w-full h-40 object-cover"
                onError={e => e.target.style.display = 'none'}
            />
        )}
        <div className="p-3">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                {new URL(metadata.url).hostname}
            </div>
            <div className="text-white font-semibold text-sm line-clamp-2 mb-1">{metadata.title}</div>
            <div className="text-gray-400 text-xs line-clamp-2">{metadata.description}</div>
        </div>
    </div>
);

const TwitterPreview = ({ metadata }) => {
    const card = metadata.twitter_card || 'summary_large_image';
    const title = metadata.twitter_title || metadata.title;
    const desc = metadata.twitter_description || metadata.description;
    const image = metadata.twitter_image || metadata.image;
    const isLarge = card === 'summary_large_image';

    return (
        <div className="border border-gray-700 rounded-2xl overflow-hidden bg-black max-w-sm">
            {isLarge ? (
                <>
                    {image && (
                        <img
                            src={image}
                            alt={title}
                            className="w-full h-48 object-cover"
                            onError={e => e.target.style.display = 'none'}
                        />
                    )}
                    <div className="p-3">
                        <div className="text-xs text-gray-500 mb-1">{new URL(metadata.url).hostname}</div>
                        <div className="text-white text-sm font-bold line-clamp-2">{title}</div>
                        <div className="text-gray-400 text-xs line-clamp-2 mt-0.5">{desc}</div>
                    </div>
                </>
            ) : (
                <div className="flex gap-3 p-3">
                    {image && (
                        <img
                            src={image}
                            alt={title}
                            width="80"
                            height="80"
                            className="w-20 h-20 object-cover rounded-xl flex-shrink-0"
                            onError={e => e.target.style.display = 'none'}
                        />
                    )}
                    <div className="min-w-0">
                        <div className="text-xs text-gray-500 mb-0.5">{new URL(metadata.url).hostname}</div>
                        <div className="text-white text-sm font-bold line-clamp-2">{title}</div>
                        <div className="text-gray-400 text-xs line-clamp-2 mt-0.5">{desc}</div>
                    </div>
                </div>
            )}
            <div className="px-3 pb-2 text-xs text-gray-600">
                Card type: <span className="text-gray-400">{card}</span>
                {metadata.twitter_creator && (
                    <span className="ml-3">Creator: <span className="text-blue-400">{metadata.twitter_creator}</span></span>
                )}
            </div>
        </div>
    );
};

const RawMetaTable = ({ metadata }) => (
    <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
            <tbody>
                {/* Favicon */}
                {metadata.favicon && (
                    <tr className="border-b border-gray-800">
                        <td className="py-1.5 pr-3 text-gray-500 whitespace-nowrap align-top w-40">favicon</td>
                        <td className="py-1.5 flex items-center gap-2">
                            <img
                                src={metadata.favicon}
                                alt="favicon"
                                width="20"
                                height="20"
                                className="w-5 h-5 object-contain"
                                onError={e => e.target.style.display = 'none'}
                            />
                            <span className="text-gray-300 break-all">{metadata.favicon}</span>
                        </td>
                    </tr>
                )}
                <MetaRow label="title" value={metadata.title} highlight />
                <MetaRow label="description" value={metadata.description} />
                <MetaRow label="keywords" value={metadata.keywords} />
                <MetaRow label="author" value={metadata.author} />
                <MetaRow label="robots" value={metadata.robots} />
                <MetaRow label="theme-color" value={metadata.theme_color} />
                <MetaRow label="canonical" value={metadata.canonical} />
                <MetaRow label="og:type" value={metadata.type} />
                <MetaRow label="og:title" value={metadata.title} highlight />
                <MetaRow label="og:description" value={metadata.description} />
                <MetaRow label="og:image" value={metadata.image} />
                <MetaRow label="og:site_name" value={metadata.site_name} />
                <MetaRow label="og:url" value={metadata.url} />
                <MetaRow label="twitter:card" value={metadata.twitter_card} highlight />
                <MetaRow label="twitter:title" value={metadata.twitter_title} highlight />
                <MetaRow label="twitter:description" value={metadata.twitter_description} />
                <MetaRow label="twitter:image" value={metadata.twitter_image} />
                <MetaRow label="twitter:creator" value={metadata.twitter_creator} />
            </tbody>
        </table>
    </div>
);

const LinkPreview = ({ url, title, className = '' }) => {
    const [metadata, setMetadata] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [activeTab, setActiveTab] = useState('google');

    const handlePreviewToggle = async () => {
        if (isVisible) {
            setIsVisible(false);
            return;
        }

        if (!metadata && !isLoading) {
            setIsLoading(true);
            setError(null);
            try {
                const fetched = await fetchLinkPreview(url);
                setMetadata(fetched);
                setIsVisible(true);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        } else {
            setIsVisible(true);
        }
    };

    return (
        <div className={`space-y-3 ${className}`}>
            {/* Toggle button */}
            <div className="flex items-center gap-2">
                <button
                    onClick={handlePreviewToggle}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700
                             disabled:bg-gray-900 disabled:cursor-not-allowed text-gray-300
                             rounded-md border border-gray-600 transition-colors"
                >
                    {isLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" />Loading...</>
                    ) : isVisible ? (
                        <><EyeOff className="w-4 h-4" />Hide Preview</>
                    ) : (
                        <><Eye className="w-4 h-4" />Preview Metadata</>
                    )}
                </button>
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm"
                >
                    <ExternalLink className="w-3 h-3" />
                    Open
                </a>
            </div>

            {/* Error */}
            {error && (
                <div className="text-xs text-red-400 px-2">Failed to load: {error}</div>
            )}

            {/* Preview panel */}
            {isVisible && metadata && (
                <div className="p-4 bg-gray-800 border border-gray-600 rounded-lg space-y-3">
                    {/* Favicon + URL bar */}
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-700">
                        {metadata.favicon && (
                            <img
                                src={metadata.favicon}
                                alt="favicon"
                                width="20"
                                height="20"
                                className="w-5 h-5 object-contain flex-shrink-0"
                                onError={e => e.target.style.display = 'none'}
                            />
                        )}
                        <span className="text-xs text-gray-400 truncate">{metadata.url}</span>
                        {metadata.theme_color && (
                            <span
                                className="w-4 h-4 rounded-full border border-gray-600 flex-shrink-0 ml-auto"
                                style={{ backgroundColor: metadata.theme_color }}
                                title={`theme-color: ${metadata.theme_color}`}
                            />
                        )}
                    </div>

                    {/* Tabs */}
                    <div className="flex items-center gap-1 flex-wrap">
                        <Tab active={activeTab === 'google'} onClick={() => setActiveTab('google')}>Google</Tab>
                        <Tab active={activeTab === 'og'} onClick={() => setActiveTab('og')}>Open Graph</Tab>
                        <Tab active={activeTab === 'twitter'} onClick={() => setActiveTab('twitter')}>Twitter / X</Tab>
                        <Tab active={activeTab === 'raw'} onClick={() => setActiveTab('raw')}>All Tags</Tab>
                    </div>

                    {/* Tab content */}
                    <div>
                        {activeTab === 'google' && <GooglePreview metadata={metadata} />}
                        {activeTab === 'og' && <OGPreview metadata={metadata} />}
                        {activeTab === 'twitter' && <TwitterPreview metadata={metadata} />}
                        {activeTab === 'raw' && <RawMetaTable metadata={metadata} />}
                    </div>

                    {metadata.isFallback && (
                        <div className="text-xs text-yellow-500/80 pt-1 border-t border-gray-700">
                            ⚠ Live fetch failed — showing generated fallback data
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LinkPreview;
